import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarPlus } from "@fortawesome/free-regular-svg-icons";
import { faInfoCircle, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import DateAccordion from "@/components/common/DateAccordion";
import DecisionsFirst from "./DecisionsFirst";
import SubsequentDecisions from "./SubsequentDecisions";
import { calculateDecisionTimes, calculateEndDateForCycles, getMinimumRollingEndDate } from "../services/waveDecisionService";
import TooltipIconButton from "@/components/common/TooltipIconButton";
import CommonSwitch from "@/components/utils/switch/CommonSwitch";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
interface DecisionsProps {
  readonly dates: CreateWaveDatesConfig;
  readonly errors: CREATE_WAVE_VALIDATION_ERROR[];
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
  readonly onRollingEnabled: () => void;
  readonly isExpanded: boolean;
  readonly setIsExpanded: (expanded: boolean) => void;
  readonly onInteraction: () => void;
}
interface DecisionsCollapsedContentProps {
  readonly totalDecisionPoints: number;
  readonly isRollingMode: boolean;
}
function DecisionsCollapsedContent(t0) {
  const $ = _c(10);
  const {
    totalDecisionPoints,
    isRollingMode
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FontAwesomeIcon icon={faCalendarPlus} className="tw-mr-2 tw-size-4 tw-text-primary-400" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Winners</p>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = totalDecisionPoints !== 1 ? "s" : "";
  let t4;
  if ($[2] !== t3 || $[3] !== totalDecisionPoints) {
    t4 = <span className="tw-text-sm tw-font-medium tw-text-iron-50">{totalDecisionPoints} announcement{t3}</span>;
    $[2] = t3;
    $[3] = totalDecisionPoints;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isRollingMode) {
    t5 = isRollingMode && <span className="tw-ml-2 tw-rounded tw-bg-blue-500/20 tw-px-1.5 tw-text-xs tw-text-blue-400">Recurring</span>;
    $[5] = isRollingMode;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="tw-flex tw-items-center tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]">{t1}<div className="tw-flex tw-flex-col">{t2}<div className="tw-flex tw-items-center">{t4}{t5}</div></div></div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
export default function Decisions(t0) {
  const $ = _c(55);
  const {
    dates,
    errors,
    setDates,
    onRollingEnabled,
    isExpanded,
    setIsExpanded,
    onInteraction
  } = t0;
  const isRollingMode = dates.isRolling;
  let t1;
  if ($[0] !== errors) {
    t1 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.RANK_DECISION_TIME_MUST_BE_IN_FUTURE);
    $[0] = errors;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasRankFutureDateError = t1;
  const shouldShowExpandedContent = isExpanded || hasRankFutureDateError;
  const totalDecisionPoints = 1 + dates.subsequentDecisions.length;
  let t2;
  if ($[2] !== dates || $[3] !== setDates) {
    t2 = decisions => {
      setDates({
        ...dates,
        subsequentDecisions: decisions
      });
    };
    $[2] = dates;
    $[3] = setDates;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleUpdateSubsequentDecisions = t2;
  let t3;
  if ($[5] !== dates || $[6] !== onRollingEnabled || $[7] !== setDates) {
    t3 = value => {
      if (value && dates.subsequentDecisions.length === 0) {
        return;
      }
      if (value) {
        onRollingEnabled();
        const twoCompleteRoundsEndDate = calculateEndDateForCycles(dates.firstDecisionTime, dates.subsequentDecisions, 2);
        const minEndDate = getMinimumRollingEndDate(dates.firstDecisionTime, dates.subsequentDecisions);
        const newEndDate = dates.endDate !== null && dates.endDate > minEndDate ? dates.endDate : twoCompleteRoundsEndDate;
        setDates({
          ...dates,
          isRolling: true,
          endDate: newEndDate
        });
      } else {
        let newEndDate_0 = dates.firstDecisionTime;
        if (dates.subsequentDecisions.length > 0) {
          const decisionTimes = calculateDecisionTimes(dates.firstDecisionTime, dates.subsequentDecisions);
          const potentialNewEndDate = decisionTimes[decisionTimes.length - 1];
          if (potentialNewEndDate !== undefined) {
            newEndDate_0 = potentialNewEndDate;
          }
        }
        setDates({
          ...dates,
          isRolling: false,
          endDate: newEndDate_0
        });
      }
    };
    $[5] = dates;
    $[6] = onRollingEnabled;
    $[7] = setDates;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleToggleSwitch = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TooltipIconButton icon={faInfoCircle} tooltipText="Schedule when winners will be announced during your wave. With recurring cycles, announcements repeat until your end date." tooltipPosition="bottom" tooltipWidth="tw-w-80" />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== setIsExpanded || $[11] !== shouldShowExpandedContent) {
    t5 = () => setIsExpanded(!shouldShowExpandedContent);
    $[10] = setIsExpanded;
    $[11] = shouldShowExpandedContent;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== isRollingMode || $[14] !== totalDecisionPoints) {
    t6 = <DecisionsCollapsedContent totalDecisionPoints={totalDecisionPoints} isRollingMode={isRollingMode} />;
    $[13] = isRollingMode;
    $[14] = totalDecisionPoints;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== hasRankFutureDateError) {
    t7 = hasRankFutureDateError && <div role="alert" className="tw-mb-3 tw-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-error/40 tw-bg-error/10 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-error"><FontAwesomeIcon icon={faTriangleExclamation} className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" /><span>First winners announcement and wave end must be in the future.</span></div>;
    $[16] = hasRankFutureDateError;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <strong>Winner announcements</strong>;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== dates.subsequentDecisions.length) {
    t9 = dates.subsequentDecisions.length === 0 && <span className="tw-text-primary-300">{" "}With a fixed schedule, the last announcement marks your wave's end date.</span>;
    $[19] = dates.subsequentDecisions.length;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <p className="tw-mb-0 tw-text-sm tw-text-iron-300">{t8} for showcasing selected creators. Set your first date, then add more if needed.{t9}</p>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-italic tw-text-iron-400">Examples: Weekly, monthly, or quarterly announcements.</p>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10) {
    t12 = <div className="tw-mb-3 tw-border-b tw-border-iron-700/50 tw-pb-3">{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t7) {
    t13 = <div className="tw-px-5 tw-pb-1 tw-pt-2">{t7}{t12}</div>;
    $[26] = t12;
    $[27] = t7;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== dates || $[30] !== setDates) {
    t14 = time => setDates({
      ...dates,
      firstDecisionTime: time
    });
    $[29] = dates;
    $[30] = setDates;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== dates.firstDecisionTime || $[33] !== dates.votingStartDate || $[34] !== t14) {
    t15 = <DecisionsFirst firstDecisionTime={dates.firstDecisionTime} setFirstDecisionTime={t14} minTimestamp={dates.votingStartDate} />;
    $[32] = dates.firstDecisionTime;
    $[33] = dates.votingStartDate;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== dates.firstDecisionTime || $[37] !== dates.subsequentDecisions || $[38] !== handleUpdateSubsequentDecisions) {
    t16 = <div className="tw-col-span-2"><SubsequentDecisions firstDecisionTime={dates.firstDecisionTime} subsequentDecisions={dates.subsequentDecisions} setSubsequentDecisions={handleUpdateSubsequentDecisions} /></div>;
    $[36] = dates.firstDecisionTime;
    $[37] = dates.subsequentDecisions;
    $[38] = handleUpdateSubsequentDecisions;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== dates.subsequentDecisions.length || $[41] !== handleToggleSwitch || $[42] !== isRollingMode) {
    t17 = dates.subsequentDecisions.length > 0 && <div className="tw-col-span-2"><div className="tw-mb-2 tw-mt-5 tw-rounded-lg tw-border tw-border-iron-700/50 tw-shadow-md"><div className="tw-flex tw-items-center tw-justify-between"><div className="tw-flex-1"><h3 className="tw-mb-1 tw-text-base tw-font-semibold tw-text-iron-50">Repeating Announcement Cycles</h3><p className="tw-mb-0 tw-text-xs tw-text-iron-400">Repeat this pattern until your wave ends</p></div><div><CommonSwitch label="Enable recurring cycles" isOn={isRollingMode} setIsOn={handleToggleSwitch} /></div></div>{isRollingMode && <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-primary-500/30 tw-bg-primary-500/20 tw-p-3 tw-shadow-inner"><p className="tw-text-primary-100 tw-mb-0 tw-text-xs"><strong>Recurring cycles enabled.</strong> Announcements will repeat until your wave's end date.</p></div>}</div></div>;
    $[40] = dates.subsequentDecisions.length;
    $[41] = handleToggleSwitch;
    $[42] = isRollingMode;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== onInteraction || $[45] !== t15 || $[46] !== t16 || $[47] !== t17) {
    t18 = <div className="tw-grid tw-grid-cols-1 tw-gap-x-10 tw-gap-y-6 tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700 tw-px-5 tw-pb-5 md:tw-grid-cols-2" onClick={onInteraction}>{t15}{t16}{t17}</div>;
    $[44] = onInteraction;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== shouldShowExpandedContent || $[50] !== t13 || $[51] !== t18 || $[52] !== t5 || $[53] !== t6) {
    t19 = <DateAccordion title="Winners Announcements" titleActions={t4} isExpanded={shouldShowExpandedContent} onToggle={t5} collapsedContent={t6}>{t13}{t18}</DateAccordion>;
    $[49] = shouldShowExpandedContent;
    $[50] = t13;
    $[51] = t18;
    $[52] = t5;
    $[53] = t6;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  return t19;
}